/**
 * Analytics (WP3-ANALYTICS, BUILD-PLAN M17): performance board, morning
 * huddle, provider scorecards, office comparison, Patient Finder, standard
 * reports, the Patient Map and the Metric Dictionary. Every number opens its formula and the rows behind it,
 * and the rows add up to the number.
 */
import { defineManifest } from '@velora/contracts';
import { defineWebModule } from '../../app/module.js';

export default defineWebModule({
  manifest: defineManifest({
    id: 'analytics',
    title: 'Analytics',
    version: '0.1.0',
    owner: 'WP3-ANALYTICS',
    entitlementKey: null,
    permissions: [
      'reports.view',
      'reports.compare',
      'money.view',
      'schedule.view',
      'settings.manage',
      'patient.edit',
      'analytics.patient_map',
    ],
    routes: [
      {
        id: 'boards',
        path: '/analytics',
        title: 'Analytics',
        officeScope: 'location-or-all',
        permission: 'reports.view',
      },
      {
        id: 'board',
        path: '/analytics/board',
        title: 'Performance board',
        officeScope: 'location-or-all',
        permission: 'reports.view',
      },
      {
        id: 'brief',
        path: '/analytics/brief',
        title: 'Weekly brief',
        officeScope: 'location-or-all',
        permission: 'reports.view',
      },
      {
        id: 'alerts',
        path: '/analytics/alerts',
        title: 'Alerts',
        officeScope: 'location-or-all',
        permission: 'reports.view',
      },
      {
        id: 'money',
        path: '/analytics/money',
        title: 'Money board',
        officeScope: 'location-or-all',
        permission: 'reports.view',
      },
      {
        id: 'huddle',
        path: '/analytics/huddle',
        title: 'Morning huddle',
        officeScope: 'location-or-all',
        permission: 'schedule.view',
        readsPhi: true,
      },
      {
        id: 'providers',
        path: '/analytics/providers',
        title: 'Provider scorecards',
        officeScope: 'location-or-all',
        permission: 'reports.view',
        // Base analytics since the DI parity build (gap #9): the Analytics Pro
        // gate dropped offices from the view without a word.
      },
      {
        id: 'practice',
        path: '/analytics/practice',
        title: 'Practice health',
        officeScope: 'location-or-all',
        permission: 'reports.view',
      },
      {
        id: 'recoverable',
        path: '/analytics/recoverable',
        title: 'Money you can recover',
        officeScope: 'location-or-all',
        permission: 'reports.view',
      },
      {
        id: 'watch',
        path: '/analytics/watch',
        title: 'Error and theft watch',
        officeScope: 'location-or-all',
        permission: 'money.view',
        readsPhi: true,
      },
      {
        id: 'pivot',
        path: '/analytics/pivot',
        title: 'Scorecards by anything',
        officeScope: 'location-or-all',
        permission: 'reports.view',
      },
      {
        id: 'compare',
        path: '/analytics/compare',
        title: 'Compare offices',
        officeScope: 'location-or-all',
        permission: 'reports.compare',
      },
      {
        id: 'finder',
        path: '/analytics/finder',
        title: 'Patient Finder',
        officeScope: 'location-or-all',
        permission: 'reports.view',
        readsPhi: true,
      },
      {
        id: 'reports',
        path: '/analytics/reports',
        title: 'Reports',
        officeScope: 'location-or-all',
        permission: 'reports.view',
        readsPhi: true,
      },
      {
        id: 'scorecard',
        path: '/analytics/scorecard',
        title: 'Scorecard',
        officeScope: 'location-or-all',
        permission: 'reports.view',
      },
      {
        id: 'schedule',
        path: '/analytics/schedule',
        title: 'Schedule analysis',
        officeScope: 'location-or-all',
        permission: 'reports.view',
      },
      {
        id: 'goals',
        path: '/analytics/goals',
        title: 'Goals',
        officeScope: 'location-or-all',
        permission: 'reports.view',
      },
      {
        id: 'office-reports',
        path: '/analytics/office-reports',
        title: 'Office reports',
        officeScope: 'location-or-all',
        permission: 'reports.view',
        readsPhi: true,
      },
      {
        id: 'day-end',
        path: '/analytics/day-end',
        title: 'Day-end reports',
        officeScope: 'location-or-all',
        permission: 'reports.view',
        readsPhi: true,
      },
      {
        id: 'year-end',
        path: '/analytics/year-end',
        title: 'Year-end report',
        officeScope: 'location-or-all',
        permission: 'reports.view',
      },
      {
        id: 'ar-party',
        path: '/analytics/ar-party',
        title: 'A/R by responsible party',
        officeScope: 'location-or-all',
        permission: 'reports.view',
        readsPhi: true,
      },
      {
        id: 'operatory',
        path: '/analytics/operatory',
        title: 'Production by operatory',
        officeScope: 'location-or-all',
        permission: 'reports.view',
      },
      {
        id: 'hygiene-pack',
        path: '/analytics/hygiene',
        title: 'Hygiene pack',
        officeScope: 'location-or-all',
        permission: 'reports.view',
      },
      {
        id: 'payer-contracts',
        path: '/analytics/payer-contracts',
        title: 'Payer contracts',
        officeScope: 'location-or-all',
        permission: 'reports.view',
        readsPhi: true,
      },
      {
        id: 'patient-map',
        path: '/analytics/patient-map',
        title: 'Patient Map',
        officeScope: 'location-or-all',
        permission: 'reports.view',
        readsPhi: true,
      },
      {
        id: 'dictionary',
        path: '/analytics/dictionary',
        title: 'Metric dictionary',
        officeScope: 'practice',
      },
    ],
    nav: [
      {
        label: 'Analytics',
        labelKey: 'reports',
        group: 'analytics',
        routeId: 'boards',
        icon: 'bar-chart-3',
        order: 10,
        permission: 'reports.view',
      },
      // Owner debrief 2026-10-28: "important to Dr. John", it was only found
      // by searching. Its own sidebar item, right after Analytics.
      {
        label: 'Morning huddle',
        group: 'analytics',
        routeId: 'huddle',
        icon: 'sun',
        order: 11,
        permission: 'schedule.view',
      },
      // A way in for each analytics page (navigation review P0): side nav,
      // module toolbar (Office Manager) and Ctrl+K.
      {
        label: 'Scorecard',
        group: 'analytics',
        routeId: 'scorecard',
        icon: 'trending-up',
        order: 11,
        permission: 'reports.view',
      },
      {
        label: 'Goals',
        group: 'analytics',
        routeId: 'goals',
        icon: 'list-checks',
        order: 12,
        permission: 'reports.view',
      },
      {
        label: 'Schedule analysis',
        group: 'analytics',
        routeId: 'schedule',
        icon: 'calendar-days',
        order: 13,
        permission: 'reports.view',
      },
      // Hayden 2026-10-28 R10: the nightly day-end pack, one click from the sidebar.
      {
        label: 'Day-end reports',
        group: 'analytics',
        routeId: 'day-end',
        icon: 'file-text',
        order: 13,
        permission: 'reports.view',
      },
      {
        label: 'Payer contracts',
        group: 'analytics',
        routeId: 'payer-contracts',
        icon: 'file-text',
        order: 14,
        permission: 'reports.view',
      },
    ],
    commands: [
      {
        id: 'analytics.open',
        title: 'Go to Analytics',
        keywords: ['reports', 'kpi', 'dashboard', 'performance', 'metrics', 'office comparison'],
        routeId: 'boards',
        permission: 'reports.view',
      },
      {
        id: 'analytics.board',
        title: 'Performance board',
        keywords: ['performance board', 'kpi tiles', 'production pace', 'office comparison'],
        routeId: 'board',
        permission: 'reports.view',
      },
      {
        id: 'analytics.brief',
        title: 'Owner weekly brief',
        keywords: ['brief', 'weekly', 'decisions', 'owner', 'monday', 'summary'],
        routeId: 'brief',
        permission: 'reports.view',
      },
      {
        id: 'analytics.alerts',
        title: 'KPI alerts',
        keywords: [
          'alerts',
          'anomaly',
          'write-off',
          'adjustment',
          'embezzlement',
          'cancellations',
          'spike',
        ],
        routeId: 'alerts',
        permission: 'reports.view',
      },
      {
        id: 'analytics.money',
        title: 'Owner money board',
        keywords: [
          'collection %',
          'days in ar',
          'ar over 90',
          'first pass',
          'acceptance',
          'benchmarks',
          'money',
        ],
        routeId: 'money',
        permission: 'reports.view',
      },
      {
        id: 'analytics.day-end',
        title: 'Day-end reports',
        keywords: [
          'day sheet',
          'deposit',
          'deleted transactions',
          'ach',
          'end of day',
          'reconciliation',
          'bank slip',
        ],
        routeId: 'day-end',
        permission: 'reports.view',
      },
      {
        id: 'analytics.year-end',
        title: 'Year-end report',
        keywords: ['year end', 'accountant', 'annual', 'tax'],
        routeId: 'year-end',
        permission: 'reports.view',
      },
      {
        id: 'analytics.ar-party',
        title: 'A/R by responsible party',
        keywords: ['aging', 'accounts receivable', 'buckets', 'responsible party', 'targets'],
        routeId: 'ar-party',
        permission: 'reports.view',
      },
      {
        id: 'analytics.operatory',
        title: 'Production by operatory',
        keywords: ['operatory', 'op room', 'chair', 'room', 'assistant'],
        routeId: 'operatory',
        permission: 'reports.view',
      },
      {
        id: 'analytics.hygiene-pack',
        title: 'Hygiene pack',
        keywords: ['hygienist', 'srp', 'perio', 'fluoride', 'sealant', 'arestin'],
        routeId: 'hygiene-pack',
        permission: 'reports.view',
      },
      {
        id: 'analytics.huddle',
        title: 'Morning huddle',
        keywords: ['huddle', 'today', 'tomorrow', 'yesterday', 'opportunities', 'morning'],
        routeId: 'huddle',
        permission: 'schedule.view',
      },
      {
        id: 'analytics.providers',
        title: 'Provider scorecards',
        keywords: ['provider', 'dentist', 'hygienist', 'scorecard', 'production per hour'],
        routeId: 'providers',
        permission: 'reports.view',
      },
      {
        id: 'analytics.recoverable',
        title:
          'Money you can recover: missed calls, unscheduled treatment, benefits, recall, openings, no-shows',
        keywords: [
          'recoverable',
          'recover',
          'missed calls',
          'lost revenue',
          'year-end benefits',
          'insurance benefits',
          'recall leakage',
          'fill rate',
          'no-show cost',
          'cancellation cost',
        ],
        routeId: 'recoverable',
        permission: 'reports.view',
      },
      {
        id: 'analytics.watch',
        title: 'Error and theft watch: deletions, backdated payments, refunds, deposits, discounts',
        keywords: [
          'embezzlement',
          'theft',
          'fraud',
          'deleted transactions',
          'backdated',
          'refunds',
          'deposit',
          'cash',
          'discounts',
          'write-offs by staff',
        ],
        routeId: 'watch',
        permission: 'money.view',
      },
      {
        id: 'analytics.practice',
        title: 'Practice health: front desk, case acceptance, money flow, growth, forecast',
        keywords: [
          'practice health',
          'speed to lead',
          'call to book',
          'front desk',
          'text response',
          'unanswered texts',
          'readiness',
          'case acceptance by price',
          'follow-up',
          'days to pay',
          'denial rate',
          'write-off',
          'payment type',
          'patients gained lost',
          'cost per new patient',
          'reviews rating',
          'forecast',
          'turned away',
          'medicaid',
        ],
        routeId: 'practice',
        permission: 'reports.view',
      },
      {
        id: 'analytics.pivot',
        title: 'Pivot scorecards: any metric by provider, code, referral, carrier, team',
        keywords: [
          'pivot',
          'scorecards',
          'by procedure code',
          'by carrier',
          'by referral source',
          'payer mix',
          'team member',
          'procedure mix',
        ],
        routeId: 'pivot',
        permission: 'reports.view',
      },
      {
        id: 'analytics.compare',
        title: 'Compare offices',
        keywords: ['office comparison', 'league table', 'rank offices', 'benchmark', 'compare'],
        routeId: 'compare',
        permission: 'reports.compare',
      },
      {
        id: 'analytics.finder',
        title: 'Patient Finder',
        keywords: ['unscheduled treatment', 'overdue recall', 'balance', 'list', 'segment'],
        routeId: 'finder',
        permission: 'reports.view',
      },
      {
        id: 'analytics.reports',
        title: 'Reports: day sheet, production, aging',
        keywords: ['day sheet', 'production', 'collections', 'aging', 'receivable', 'end of day'],
        routeId: 'reports',
        permission: 'reports.view',
      },
      {
        id: 'analytics.scorecard',
        title: 'Scorecard',
        keywords: ['scorecard', 'kpi', 'trend', 'goal', 'locations'],
        routeId: 'scorecard',
        permission: 'reports.view',
      },
      {
        id: 'analytics.schedule',
        title: 'Schedule analysis',
        keywords: ['schedule', 'production vs goal', 'week', 'day', 'pacing'],
        routeId: 'schedule',
        permission: 'reports.view',
      },
      {
        id: 'analytics.goals',
        title: 'Goals',
        keywords: ['goals', 'targets', 'clinical hours', 'daily goal', 'definitions'],
        routeId: 'goals',
        permission: 'reports.view',
      },
      {
        id: 'analytics.officeReports',
        title: 'Office reports: deposit slip, analysis summary, month end',
        keywords: ['deposit slip', 'deposit', 'month end', 'close month', 'analysis summary'],
        routeId: 'office-reports',
        permission: 'reports.view',
      },
      {
        id: 'analytics.payerContracts',
        title: 'Payer contracts: PPO renegotiation pack, paid below contract',
        keywords: [
          'ppo',
          'renegotiate',
          'renegotiation',
          'fee schedule',
          'ucr',
          'write-off',
          'underpaid',
          'below contract',
          'appeal',
        ],
        routeId: 'payer-contracts',
        permission: 'reports.view',
      },
      {
        id: 'analytics.patient-map',
        title: 'Patient Map',
        keywords: [
          'map',
          'where patients live',
          'zip',
          'heatmap',
          'lapsed',
          'market',
          'competitors',
        ],
        routeId: 'patient-map',
        permission: 'reports.view',
      },
      {
        id: 'analytics.dictionary',
        title: 'Metric dictionary',
        keywords: ['definitions', 'formula', 'how is it calculated', 'glossary'],
        routeId: 'dictionary',
      },
    ],
    shortcuts: [{ keys: 'g a', description: 'Go to Analytics', routeId: 'boards' }],
  }),
  pages: {
    boards: () => import('./pages/OverviewPage.js'),
    board: () => import('./pages/BoardPage.js'),
    brief: () => import('./pages/BriefPage.js'),
    alerts: () => import('./pages/AlertsPage.js'),
    money: () => import('./pages/MoneyBoardPage.js'),
    huddle: () => import('./pages/HuddlePage.js'),
    providers: () => import('./pages/ProvidersPage.js'),
    compare: () => import('./pages/ComparePage.js'),
    pivot: () => import('./pages/PivotPage.js'),
    recoverable: () => import('./pages/RecoverablePage.js'),
    watch: () => import('./pages/WatchPage.js'),
    practice: () => import('./pages/PracticeHealthPage.js'),
    finder: () => import('./pages/FinderPage.js'),
    reports: () => import('./pages/ReportsPage.js'),
    scorecard: () => import('./pages/ScorecardPage.js'),
    schedule: () => import('./pages/SchedulePage.js'),
    goals: () => import('./pages/GoalsPage.js'),
    'office-reports': () => import('./pages/OfficeReportsPage.js'),
    'day-end': () => import('./pages/DayEndPage.js'),
    'year-end': () => import('./pages/YearEndPage.js'),
    'ar-party': () => import('./pages/ArPartyPage.js'),
    operatory: () => import('./pages/OperatoryPage.js'),
    'hygiene-pack': () => import('./pages/HygienePackPage.js'),
    'payer-contracts': () => import('./pages/PayerContractsPage.js'),
    'patient-map': () => import('./pages/PatientMapPage.js'),
    dictionary: () => import('./pages/DictionaryPage.js'),
  },
});
